/**
 * communication utilities
 * 
 * @author    Jens Gruschel
 * @copyright © 2018 contexagon GmbH
 */


import {finishActivityAction, startActivityAction} from "../redux/activity/actions";
import {showMessageAction, showDialogMessageAction} from "../redux/message/actions";
import {logoutAction} from "../redux/authorization/actions";
import {generateActivityId} from "../utils";
import {sleep} from "../utils";


export function executeSimpleServerCommand(description, method, path, body) {

    return async (dispatch, getState) => {

        executeJSONCommandActivity(dispatch, description, path, {
            method: method,
            body: body ? JSON.stringify(body) : undefined,
            headers: {
                "Content-Type": "application/json",
                "Authorization": "Bearer " + getState().authorization.accessToken
            }
        });
    }
}


export function showServerCommandMessage(title, response) {

    return async (dispatch) => {

        showJSONResponse(dispatch, title, response);
    }
}


/**
 * asynchronously fetch JSON data from server and handle errors (such as 404 or invalid JSON format)
 */
export async function fetchJSON(path, options, onError = null) {

    // perform server communication
    const logging = true; // TODO
    if (logging) console.log("fetch:", path, options);
    const response = await fetch(path, options);
    if (logging) console.log("response:", response);
    if (response.status >= 400) {
        let text = "";
        try {
            // get error message text from JSON error object if possible
            // (without cloning the response it is not possible to get the response text afterwards,
            // because the "body has already been consumed")
            const data = await response.clone().json();
            text = data.message || data.msg || await response.text();
        }
        catch (error) {
            // no valid JSON error object
            text = await response.text();
            console.log("server returned non-json answer: ", text);
        }
        if (onError) onError(response.status, text);
        throw new Error("error " + response.status + ": " + response.statusText + " (" + text + ")");
    }

    // parse response
    try {
        const data = await response.json();
        return data;
    }
    catch (error) {
        throw new Error("invalid server response");
    }
}

/**
 * asynchronously fetch JSON data from server and handle errors (such as 404 or invalid JSON format),
 * while opening and closing an activity with a given description
 */
export async function fetchJSONActivity(dispatch, description, path, options) {

    // indicate start of activity
    const activityId = generateActivityId();
    dispatch(startActivityAction(activityId, description));

    // wait a bit to simulate slow connections
    const simulateSlowConnection = false;
    if (simulateSlowConnection) {
        await sleep(1000);
    }

    try {
        // perform server communication and parse response
        const response = await fetchJSON(path, options, (status, text) => {
            if (status === 401) dispatch(logoutAction());
        });
        return response;
    }
    finally {
        // don't forget to indicate end of activity
        dispatch(finishActivityAction(activityId));
    }
}

/**
 * asynchronously fetch JSON data from server and handle errors (such as 404 or invalid JSON format)
 * by showing a message
 */
export async function executeJSONCommand(dispatch, path, options) {

    try {
        const response = await fetchJSON(path, options, (status, text) => {
            if (status === 401) dispatch(logoutAction());
        });
        showJSONResponse(dispatch, "command response", response);
    }
    catch (error) {
        console.log(error);
        dispatch(showDialogMessageAction("error executing command", error.message));
    }
}

/**
 * asynchronously fetch JSON data from server while opening and closing an activity with a given description
 * and handle errors (such as 404 or invalid JSON format) by showing a message
 */
export async function executeJSONCommandActivity(dispatch, description, path, options) {

    try {
        const response = await fetchJSONActivity(dispatch, description, path, options);
        showJSONResponse(dispatch, "command response", response);
    }
    catch (error) {
        console.log(error);
        dispatch(showDialogMessageAction("error executing command", error.message));
    }
}

/**
 * show message for JSON response received by server
 */
export function showJSONResponse(dispatch, title, response) {
    const jsonText = JSON.stringify(response, null, 4);
    if (response.level === "hint" || response.level === "warning") {
        // show hint or warning as dialog
        const text = response.message || jsonText;
        dispatch(showDialogMessageAction(title, text));
    }
    else if (jsonText.length < 200) {
        // show simple success message briefly
        const text = response.message || jsonText;
        dispatch(showMessageAction(text));
    }
    else {
        // show longer responses as dialog
        dispatch(showDialogMessageAction(title, jsonText));
    }
}
